import React, { useState, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { getStickers, updateStickers, uploadStickerImage } from '../services/stickerService';
import { getCategories } from '../services/categoryService';
import ProductSticker from './ProductSticker';
import sampleCardImg from '../assets/default.png';

export default function AdminStickersTab({ categories: propCategories }) {
  const [stickers, setStickers] = useState([]);
  const [categories, setCategories] = useState(propCategories || []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Editor State
  const [editingSticker, setEditingSticker] = useState(null); // null when not editing, object when editing/creating
  const [isNew, setIsNew] = useState(false);

  // Category search in editor
  const [catSearch, setCatSearch] = useState('');
  // SKU bulk input in editor
  const [bulkSkuInput, setBulkSkuInput] = useState('');
  const [skuSearch, setSkuSearch] = useState('');

  const fileInputRef = useRef(null);

  // Load stickers and categories
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const [stks, cats] = await Promise.all([
          getStickers(true).catch(() => []),
          propCategories ? Promise.resolve(propCategories) : getCategories().catch(() => [])
        ]);
        if (!mounted) return;
        setStickers(Array.isArray(stks) ? stks : []);
        if (Array.isArray(cats) && cats.length) {
          setCategories(cats);
        }
      } catch (err) {
        console.error('Failed to load stickers tab data:', err);
        toast.error('Dështoi ngarkimi i të dhënave të stickers');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [propCategories]);

  // Flatten categories hierarchy for quick lookup and selection
  const { flatCategories, categoryMap } = useMemo(() => {
    const flat = [];
    const map = new Map();

    function walk(nodes, depth = 0, path = []) {
      if (!Array.isArray(nodes)) return;
      for (const n of nodes) {
        if (!n || !n.id) continue;
        const currentPath = [...path, n.name];
        const item = {
          id: String(n.id),
          name: n.name,
          depth,
          path: currentPath,
          subcategories: Array.isArray(n.subcategories) ? n.subcategories : []
        };
        flat.push(item);
        map.set(item.id, item);
        if (item.subcategories.length) {
          walk(item.subcategories, depth + 1, currentPath);
        }
      }
    }
    walk(categories);
    return { flatCategories: flat, categoryMap: map };
  }, [categories]);

  // Filter categories in editor search
  const filteredCategories = useMemo(() => {
    if (!catSearch.trim()) return flatCategories;
    const q = catSearch.toLowerCase().trim();
    return flatCategories.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.id.includes(q) ||
      c.path.some(p => p.toLowerCase().includes(q))
    );
  }, [flatCategories, catSearch]);

  // Parse bulk SKUs in candidate box
  const parsedCandidateSkus = useMemo(() => {
    if (!bulkSkuInput.trim()) return [];
    const tokens = bulkSkuInput
      .split(/[\r\n,;\s]+/)
      .map(s => s.trim().toUpperCase())
      .filter(s => s.length >= 2);
    return Array.from(new Set(tokens));
  }, [bulkSkuInput]);

  // Create new sticker template
  const handleAddNew = () => {
    const newStk = {
      id: `stk_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      name: 'Sticker i Ri',
      enabled: true,
      type: 'text', // 'text' | 'image'
      // Text options
      text: 'SUPER ÇMIM',
      bgColor: '#e11d48',
      textColor: '#ffffff',
      fontSize: 11,
      fontWeight: 700,
      borderRadius: 6,
      paddingX: 9,
      paddingY: 4,
      shadow: true,
      borderColor: '',
      borderWidth: 0,
      // Image options
      imageUrl: '',
      imageWidth: 65,
      imageHeight: null,
      // Positioning & Directional Pixels
      position: 'top-left', // 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
      offsetX: 10,
      offsetY: 10,
      rotation: 0,
      zIndex: 12,
      // Targets
      categoryIds: [],
      includeSubcategories: true,
      skus: []
    };
    setEditingSticker(newStk);
    setIsNew(true);
    setBulkSkuInput('');
    setCatSearch('');
    setSkuSearch('');
  };

  const handleEdit = (stk) => {
    setEditingSticker(JSON.parse(JSON.stringify(stk)));
    setIsNew(false);
    setBulkSkuInput('');
    setCatSearch('');
    setSkuSearch('');
  };

  const handleToggleEnable = async (stkId) => {
    const updated = stickers.map(s =>
      s.id === stkId ? { ...s, enabled: !s.enabled } : s
    );
    setStickers(updated);
    try {
      await updateStickers(updated);
      toast.success('Statusi i sticker-it u përditësua');
    } catch (err) {
      console.error(err);
      toast.error('Dështoi përditësimi i statusit');
    }
  };

  const handleDuplicate = async (stk) => {
    const copy = {
      ...JSON.parse(JSON.stringify(stk)),
      id: `stk_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      name: `${stk.name} (Kopje)`
    };
    const updated = [...stickers, copy];
    setStickers(updated);
    try {
      await updateStickers(updated);
      toast.success(`Sticker "${copy.name}" u krijua me sukses`);
    } catch (err) {
      console.error(err);
      toast.error('Dështoi duplikimi i sticker-it');
    }
  };

  const handleDelete = async (stkId, name) => {
    if (!window.confirm(`Jeni të sigurt që dëshironi të fshini sticker-in "${name}"?`)) {
      return;
    }
    const updated = stickers.filter(s => s.id !== stkId);
    setStickers(updated);
    if (editingSticker && editingSticker.id === stkId) {
      setEditingSticker(null);
    }
    try {
      await updateStickers(updated);
      toast.success(`Sticker-i "${name}" u fshi`);
    } catch (err) {
      console.error(err);
      toast.error('Dështoi fshirja e sticker-it');
    }
  };

  // Image Upload handler
  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const res = await uploadStickerImage(file);
      if (res && res.url) {
        setEditingSticker(prev => ({
          ...prev,
          imageUrl: res.url,
          type: 'image'
        }));
        toast.success('Imazhi i sticker-it u ngarkua me sukses!');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Dështoi ngarkimi i imazhit');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // SKU Management inside Editor
  const handleAddCandidateSkus = (e) => {
    e.preventDefault();
    if (!parsedCandidateSkus.length || !editingSticker) return;

    const currentSkus = new Set((editingSticker.skus || []).map(s => String(s).toUpperCase()));
    let addedCount = 0;
    for (const s of parsedCandidateSkus) {
      if (!currentSkus.has(s)) {
        currentSkus.add(s);
        addedCount++;
      }
    }

    setEditingSticker(prev => ({
      ...prev,
      skus: Array.from(currentSkus)
    }));
    setBulkSkuInput('');
    toast.success(`U shtuan ${addedCount} kode SKU`);
  };

  const handleRemoveSkuFromEditor = (skuToRemove) => {
    setEditingSticker(prev => ({
      ...prev,
      skus: (prev.skus || []).filter(s => s !== skuToRemove)
    }));
  };

  const handleClearAllSkusFromEditor = () => {
    setEditingSticker(prev => ({ ...prev, skus: [] }));
  };

  // Category Management inside Editor
  const handleToggleCategory = (catId) => {
    setEditingSticker(prev => {
      const current = new Set((prev.categoryIds || []).map(String));
      if (current.has(String(catId))) {
        current.delete(String(catId));
      } else {
        current.add(String(catId));
      }
      return { ...prev, categoryIds: Array.from(current) };
    });
  };

  const handleRemoveCategoryFromEditor = (catId) => {
    setEditingSticker(prev => ({
      ...prev,
      categoryIds: (prev.categoryIds || []).filter(id => String(id) !== String(catId))
    }));
  };

  // Save changes to current sticker
  const handleSaveSticker = async (e) => {
    e?.preventDefault();
    if (!editingSticker) return;

    if (!editingSticker.name?.trim()) {
      toast.error('Ju lutem vendosni një emër për sticker-in');
      return;
    }

    if (editingSticker.type === 'text' && !editingSticker.text?.trim()) {
      toast.error('Ju lutem vendosni tekstin e sticker-it');
      return;
    }

    if (editingSticker.type === 'image' && !editingSticker.imageUrl?.trim()) {
      toast.error('Ju lutem ngarkoni një imazh ose vendosni URL-në e tij');
      return;
    }

    try {
      setSaving(true);
      let nextStickers;
      if (isNew) {
        nextStickers = [...stickers, editingSticker];
      } else {
        nextStickers = stickers.map(s => s.id === editingSticker.id ? editingSticker : s);
      }

      const res = await updateStickers(nextStickers);
      setStickers(res.stickers || nextStickers);
      setEditingSticker(null);
      setIsNew(false);
      toast.success(isNew ? 'Sticker-i u krijua me sukses!' : 'Sticker-i u ruajt me sukses!');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Dështoi ruajtja e sticker-it');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: '#64748b' }}>
        Duke ngarkuar konfigurimin e stickers...
      </div>
    );
  }

  // PRESET COLORS
  const colorPresets = [
    { label: 'Kuqe', bg: '#e11d48', text: '#ffffff' },
    { label: 'Portokalli', bg: '#f97316', text: '#ffffff' },
    { label: 'Teal Globe', bg: '#007da5', text: '#ffffff' },
    { label: 'Blu Marinë', bg: '#0284c7', text: '#ffffff' },
    { label: 'Vjollcë', bg: '#8b5cf6', text: '#ffffff' },
    { label: 'Jeshile', bg: '#10b981', text: '#ffffff' },
    { label: 'Zezë', bg: '#0f172a', text: '#ffffff' },
    { label: 'E verdhë', bg: '#facc15', text: '#0f172a' }
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 24 }}>

      {/* Top Banner / Actions Bar */}
      <div className="admin-card" style={{ padding: 22, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>🏷️ Stickers mbi Kartat e Produkteve</span>
            <span style={{ fontSize: 12, fontWeight: 700, background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '2px 10px', borderRadius: 12 }}>
              {stickers.length} {stickers.length === 1 ? 'sticker' : 'stickers'}
            </span>
          </h3>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
            Krijoni etiketa me tekst ose imazh, pozicionojini me piksela të sakta në cilindo kënd të kartës, dhe caktojini sipas kategorive ose kodeve SKU.
          </p>
        </div>

        {!editingSticker && (
          <button
            type="button"
            onClick={handleAddNew}
            style={{
              background: '#007da5',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,125,165,0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              transition: 'background 0.15s ease'
            }}
          >
            <span>+ Shto Sticker të Ri</span>
          </button>
        )}
      </div>

      {/* IF EDITING / CREATING: Render Sticker Editor Panel */}
      {editingSticker && (
        <div className="admin-card" style={{ padding: 24, background: '#fff', borderRadius: 14, border: '2px solid #007da5', boxShadow: '0 8px 30px rgba(0,125,165,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                {isNew ? '✨ Krijo Sticker të Ri' : `✏️ Ndrysho Sticker-in: ${editingSticker.name}`}
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                Konfiguroni dizajnin, vendndodhjen me piksela dhe produktet e synuara.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => setEditingSticker(null)}
                style={{
                  background: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  padding: '8px 16px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Anulo
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleSaveSticker}
                style={{
                  background: '#007da5',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 22px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  boxShadow: '0 2px 6px rgba(0,125,165,0.25)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                {saving ? 'Duke ruajtur...' : '💾 Ruaj Sticker-in'}
              </button>
            </div>
          </div>

          {/* Editor Grid: Settings Form on Left (2 cols) & Live Card Preview on Right (1 col) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 28, alignItems: 'start' }}>

            {/* LEFT COLUMN: Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

              {/* 1. Emri & Statusi */}
              <div style={{ background: '#f8fafc', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                      Emri i Sticker-it (për menaxhim të brendshëm)
                    </label>
                    <input
                      type="text"
                      className="admin-input"
                      value={editingSticker.name || ''}
                      onChange={(e) => setEditingSticker(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="p.sh. Oferte Verore 2026, Black Friday, Super Çmim..."
                      style={{ background: '#fff' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                      Statusi
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: '#fff', padding: '7px 14px', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(editingSticker.enabled)}
                        onChange={(e) => setEditingSticker(prev => ({ ...prev, enabled: e.target.checked }))}
                        style={{ width: 16, height: 16, accentColor: '#007da5', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 700, color: editingSticker.enabled ? '#15803d' : '#94a3b8' }}>
                        {editingSticker.enabled ? 'AKTIV' : 'JO-AKTIV'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* 2. Lloji i Sticker-it: Tekst Badge vs Imazh */}
              <div style={{ background: '#f8fafc', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
                  Lloji i Sticker-it
                </label>
                <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                  <button
                    type="button"
                    onClick={() => setEditingSticker(prev => ({ ...prev, type: 'text' }))}
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: editingSticker.type === 'text' ? '2px solid #007da5' : '1px solid #cbd5e1',
                      background: editingSticker.type === 'text' ? '#f0f9ff' : '#fff',
                      color: editingSticker.type === 'text' ? '#007da5' : '#475569',
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    <span>🏷️ Tekst (Badge i Stiluar)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingSticker(prev => ({ ...prev, type: 'image' }))}
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: editingSticker.type === 'image' ? '2px solid #007da5' : '1px solid #cbd5e1',
                      background: editingSticker.type === 'image' ? '#f0f9ff' : '#fff',
                      color: editingSticker.type === 'image' ? '#007da5' : '#475569',
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    <span>🖼️ Imazh (PNG, SVG, Grafikë)</span>
                  </button>
                </div>

                {/* Sub-options for TEXT */}
                {editingSticker.type === 'text' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 6 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                        Teksti i Etiketës
                      </label>
                      <input
                        type="text"
                        className="admin-input"
                        value={editingSticker.text || ''}
                        onChange={(e) => setEditingSticker(prev => ({ ...prev, text: e.target.value }))}
                        placeholder="p.sh. SUPER ÇMIM, FUSHATË, -30%, BESTSELLER"
                        style={{ background: '#fff', fontWeight: 700 }}
                      />
                    </div>

                    {/* Color controls */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                          Ngjyra e Sfondit
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="color"
                            value={editingSticker.bgColor || '#e11d48'}
                            onChange={(e) => setEditingSticker(prev => ({ ...prev, bgColor: e.target.value }))}
                            style={{ width: 38, height: 36, padding: 0, border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}
                          />
                          <input
                            type="text"
                            value={editingSticker.bgColor || '#e11d48'}
                            onChange={(e) => setEditingSticker(prev => ({ ...prev, bgColor: e.target.value }))}
                            style={{ width: 90, padding: '7px 8px', fontSize: 12, fontWeight: 700, borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff' }}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                          Ngjyra e Tekstit
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="color"
                            value={editingSticker.textColor || '#ffffff'}
                            onChange={(e) => setEditingSticker(prev => ({ ...prev, textColor: e.target.value }))}
                            style={{ width: 38, height: 36, padding: 0, border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}
                          />
                          <input
                            type="text"
                            value={editingSticker.textColor || '#ffffff'}
                            onChange={(e) => setEditingSticker(prev => ({ ...prev, textColor: e.target.value }))}
                            style={{ width: 90, padding: '7px 8px', fontSize: 12, fontWeight: 700, borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Presets chips */}
                    <div>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Kombinime të Shpejta:</span>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                        {colorPresets.map((cp) => (
                          <button
                            key={cp.label}
                            type="button"
                            onClick={() => setEditingSticker(prev => ({ ...prev, bgColor: cp.bg, textColor: cp.text }))}
                            style={{
                              background: cp.bg,
                              color: cp.text,
                              border: '1px solid rgba(0,0,0,0.1)',
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: 'pointer'
                            }}
                          >
                            {cp.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Size & Padding controls */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, paddingTop: 4 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 3 }}>
                          Madhësia e Fontit
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="number"
                            min="9"
                            max="32"
                            value={editingSticker.fontSize || 11}
                            onChange={(e) => setEditingSticker(prev => ({ ...prev, fontSize: Number(e.target.value) || 11 }))}
                            style={{ width: 60, padding: '5px 6px', textAlign: 'center', fontWeight: 700, borderRadius: 6, border: '1px solid #cbd5e1' }}
                          />
                          <span style={{ fontSize: 11, color: '#64748b' }}>px</span>
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 3 }}>
                          Këndet (Radius)
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="number"
                            min="0"
                            max="40"
                            value={editingSticker.borderRadius ?? 6}
                            onChange={(e) => setEditingSticker(prev => ({ ...prev, borderRadius: Number(e.target.value) || 0 }))}
                            style={{ width: 60, padding: '5px 6px', textAlign: 'center', fontWeight: 700, borderRadius: 6, border: '1px solid #cbd5e1' }}
                          />
                          <span style={{ fontSize: 11, color: '#64748b' }}>px</span>
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 3 }}>
                          Hapësira X
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="number"
                            min="2"
                            max="40"
                            value={editingSticker.paddingX || 8}
                            onChange={(e) => setEditingSticker(prev => ({ ...prev, paddingX: Number(e.target.value) || 8 }))}
                            style={{ width: 60, padding: '5px 6px', textAlign: 'center', fontWeight: 700, borderRadius: 6, border: '1px solid #cbd5e1' }}
                          />
                          <span style={{ fontSize: 11, color: '#64748b' }}>px</span>
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 3 }}>
                          Hapësira Y
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="number"
                            min="1"
                            max="25"
                            value={editingSticker.paddingY || 4}
                            onChange={(e) => setEditingSticker(prev => ({ ...prev, paddingY: Number(e.target.value) || 4 }))}
                            style={{ width: 60, padding: '5px 6px', textAlign: 'center', fontWeight: 700, borderRadius: 6, border: '1px solid #cbd5e1' }}
                          />
                          <span style={{ fontSize: 11, color: '#64748b' }}>px</span>
                        </div>
                      </div>
                    </div>

                    {/* Shadow toggle */}
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#475569' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(editingSticker.shadow)}
                        onChange={(e) => setEditingSticker(prev => ({ ...prev, shadow: e.target.checked }))}
                        style={{ accentColor: '#007da5' }}
                      />
                      <span>Aktivizo Hijen (Box Shadow) për dukshmëri maksimale</span>
                    </label>
                  </div>
                )}

                {/* Sub-options for IMAGE */}
                {editingSticker.type === 'image' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 6 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                        Ngarko Skedar Imazhi (PNG me sfond transparent, SVG, ose WebP)
                      </label>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          onChange={handleImageFileChange}
                          style={{ display: 'none' }}
                          id="sticker-file-input"
                        />
                        <button
                          type="button"
                          disabled={uploading}
                          onClick={() => fileInputRef.current?.click()}
                          style={{
                            background: '#e0f2fe',
                            color: '#0369a1',
                            border: '1px solid #bae6fd',
                            padding: '8px 16px',
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                        >
                          <span>📁 {uploading ? 'Duke ngarkuar...' : 'Zgjidh Skedar nga Pajisja'}</span>
                        </button>
                        <span style={{ fontSize: 12, color: '#64748b' }}>ose vendosni URL direkt më poshtë:</span>
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                        URL ose Shtegu i Imazhit
                      </label>
                      <input
                        type="text"
                        className="admin-input"
                        value={editingSticker.imageUrl || ''}
                        onChange={(e) => setEditingSticker(prev => ({ ...prev, imageUrl: e.target.value }))}
                        placeholder="/uploads/stickers/... ose https://..."
                        style={{ background: '#fff', fontFamily: 'monospace', fontSize: 12 }}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                          Gjerësia e Imazhit (Width)
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="range"
                            min="25"
                            max="200"
                            step="5"
                            value={editingSticker.imageWidth || 65}
                            onChange={(e) => setEditingSticker(prev => ({ ...prev, imageWidth: Number(e.target.value) || 65 }))}
                            style={{ width: 140, accentColor: '#007da5' }}
                          />
                          <input
                            type="number"
                            min="20"
                            max="300"
                            value={editingSticker.imageWidth || 65}
                            onChange={(e) => setEditingSticker(prev => ({ ...prev, imageWidth: Number(e.target.value) || 65 }))}
                            style={{ width: 60, padding: '4px 6px', textAlign: 'center', fontWeight: 700, borderRadius: 6, border: '1px solid #cbd5e1' }}
                          />
                          <span style={{ fontSize: 12, color: '#64748b' }}>px</span>
                        </div>
                      </div>

                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#475569', marginTop: 18 }}>
                        <input
                          type="checkbox"
                          checked={Boolean(editingSticker.shadow)}
                          onChange={(e) => setEditingSticker(prev => ({ ...prev, shadow: e.target.checked }))}
                          style={{ accentColor: '#007da5' }}
                        />
                        <span>Hije rreth grafikut (Drop Shadow)</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* 3. Pozicionimi & Zhvendosja me Piksela */}
              <div style={{ background: '#f8fafc', padding: 18, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                    🎯 Pozicioni në Kartë &amp; Zhvendosja Manuale me Piksela (Pixels)
                  </label>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>
                    Zgjidhni këndin bazë të kartës dhe përcaktoni manualisht distancën e saktë në piksela (X dhe Y).
                  </p>
                </div>

                {/* 4 Corner Selector Buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
                  {[
                    { id: 'top-left', label: '↖️ Lart - Majtas (Top-Left)', icon: '↖️' },
                    { id: 'top-right', label: '↗️ Lart - Djathtas (Top-Right)', icon: '↗️' },
                    { id: 'bottom-left', label: '↙️ Poshtë - Majtas (Bottom-Left)', icon: '↙️' },
                    { id: 'bottom-right', label: '↘️ Poshtë - Djathtas (Bottom-Right)', icon: '↘️' }
                  ].map(pos => {
                    const isSelected = (editingSticker.position || 'top-left') === pos.id;
                    return (
                      <button
                        key={pos.id}
                        type="button"
                        onClick={() => setEditingSticker(prev => ({ ...prev, position: pos.id }))}
                        style={{
                          padding: '10px 14px',
                          borderRadius: 8,
                          border: isSelected ? '2px solid #007da5' : '1px solid #cbd5e1',
                          background: isSelected ? '#e0f2fe' : '#fff',
                          color: isSelected ? '#0369a1' : '#334155',
                          fontWeight: 700,
                          fontSize: 12.5,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <span style={{ fontSize: 16 }}>{pos.icon}</span>
                        <span>{pos.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Pixel Offsets (Directional Inputs) */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, background: '#fff', padding: 14, borderRadius: 8, border: '1px solid #e2e8f0' }}>

                  {/* Vertical Offset (Y) */}
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                      {editingSticker.position?.startsWith('bottom')
                        ? 'Distanca nga Poshtë (Bottom px):'
                        : 'Distanca nga Lart (Top px):'}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => setEditingSticker(prev => ({ ...prev, offsetY: Math.max(0, (Number(prev.offsetY) || 0) - 5) }))}
                        style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700, cursor: 'pointer' }}
                        title="Zvogëlo me 5px"
                      >
                        -5
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingSticker(prev => ({ ...prev, offsetY: Math.max(0, (Number(prev.offsetY) || 0) - 1) }))}
                        style={{ width: 28, height: 32, borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700, cursor: 'pointer' }}
                        title="Zvogëlo me 1px"
                      >
                        -1
                      </button>
                      <input
                        type="number"
                        min="0"
                        max="200"
                        value={editingSticker.offsetY ?? 10}
                        onChange={(e) => setEditingSticker(prev => ({ ...prev, offsetY: Math.max(0, Number(e.target.value) || 0) }))}
                        style={{ width: 65, height: 32, padding: '4px 6px', textAlign: 'center', fontWeight: 800, fontSize: 14, borderRadius: 6, border: '1px solid #cbd5e1', color: '#007da5' }}
                      />
                      <button
                        type="button"
                        onClick={() => setEditingSticker(prev => ({ ...prev, offsetY: (Number(prev.offsetY) || 0) + 1 }))}
                        style={{ width: 28, height: 32, borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700, cursor: 'pointer' }}
                        title="Rrit me 1px"
                      >
                        +1
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingSticker(prev => ({ ...prev, offsetY: (Number(prev.offsetY) || 0) + 5 }))}
                        style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700, cursor: 'pointer' }}
                        title="Rrit me 5px"
                      >
                        +5
                      </button>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>px</span>
                    </div>
                  </div>

                  {/* Horizontal Offset (X) */}
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                      {editingSticker.position?.endsWith('right')
                        ? 'Distanca nga Djathtas (Right px):'
                        : 'Distanca nga Majtas (Left px):'}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => setEditingSticker(prev => ({ ...prev, offsetX: Math.max(0, (Number(prev.offsetX) || 0) - 5) }))}
                        style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700, cursor: 'pointer' }}
                        title="Zvogëlo me 5px"
                      >
                        -5
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingSticker(prev => ({ ...prev, offsetX: Math.max(0, (Number(prev.offsetX) || 0) - 1) }))}
                        style={{ width: 28, height: 32, borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700, cursor: 'pointer' }}
                        title="Zvogëlo me 1px"
                      >
                        -1
                      </button>
                      <input
                        type="number"
                        min="0"
                        max="200"
                        value={editingSticker.offsetX ?? 10}
                        onChange={(e) => setEditingSticker(prev => ({ ...prev, offsetX: Math.max(0, Number(e.target.value) || 0) }))}
                        style={{ width: 65, height: 32, padding: '4px 6px', textAlign: 'center', fontWeight: 800, fontSize: 14, borderRadius: 6, border: '1px solid #cbd5e1', color: '#007da5' }}
                      />
                      <button
                        type="button"
                        onClick={() => setEditingSticker(prev => ({ ...prev, offsetX: (Number(prev.offsetX) || 0) + 1 }))}
                        style={{ width: 28, height: 32, borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700, cursor: 'pointer' }}
                        title="Rrit me 1px"
                      >
                        +1
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingSticker(prev => ({ ...prev, offsetX: (Number(prev.offsetX) || 0) + 5 }))}
                        style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700, cursor: 'pointer' }}
                        title="Rrit me 5px"
                      >
                        +5
                      </button>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>px</span>
                    </div>
                  </div>

                </div>

                {/* Quick Presets & Rotation */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Parazgjedhje pikselash:</span>
                    <button
                      type="button"
                      onClick={() => setEditingSticker(prev => ({ ...prev, offsetX: 10, offsetY: 10 }))}
                      style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Kënd Standard (10px, 10px)
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingSticker(prev => ({ ...prev, offsetX: 4, offsetY: 4 }))}
                      style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Në Skaj (4px, 4px)
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingSticker(prev => ({ ...prev, offsetX: 16, offsetY: 16 }))}
                      style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Më Brenda (16px, 16px)
                    </button>
                  </div>

                  {/* Rotation control */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Rrotullimi:</span>
                    <input
                      type="range"
                      min="-30"
                      max="30"
                      step="1"
                      value={editingSticker.rotation || 0}
                      onChange={(e) => setEditingSticker(prev => ({ ...prev, rotation: Number(e.target.value) || 0 }))}
                      style={{ width: 80, accentColor: '#007da5' }}
                    />
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#334155', minWidth: 28 }}>
                      {editingSticker.rotation || 0}°
                    </span>
                    {editingSticker.rotation !== 0 && (
                      <button
                        type="button"
                        onClick={() => setEditingSticker(prev => ({ ...prev, rotation: 0 }))}
                        style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11 }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* 4. Synimi i Produkteve: Sipas Kategorive & Sipas SKU */}
              <div style={{ background: '#f8fafc', padding: 18, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                  🎯 Produktet ku do të Shfaqet ky Sticker
                </h4>
                <p style={{ margin: '0 0 14px', fontSize: 12, color: '#64748b' }}>
                  Zgjidhni kategoritë dhe/ose vendosni kode specifike SKU të produkteve. Sticker-i do t'i shfaqet çdo produkti që plotëson njërën nga këto kushte.
                </p>

                {/* SECTION A: Category Selector */}
                <div style={{ background: '#fff', padding: 14, borderRadius: 8, border: '1px solid #cbd5e1', marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                      📂 Sipas Kategorive ({(editingSticker.categoryIds || []).length} të zgjedhura)
                    </div>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#0369a1', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={editingSticker.includeSubcategories !== false}
                        onChange={(e) => setEditingSticker(prev => ({ ...prev, includeSubcategories: e.target.checked }))}
                        style={{ accentColor: '#007da5' }}
                      />
                      <span>Përfshij automatikisht edhe të gjitha nënkategoritë</span>
                    </label>
                  </div>

                  {/* Selected categories pills */}
                  {(editingSticker.categoryIds || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10, padding: 8, background: '#f8fafc', borderRadius: 6 }}>
                      {(editingSticker.categoryIds || []).map(cid => {
                        const cat = categoryMap.get(String(cid));
                        return (
                          <div
                            key={cid}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              background: '#e0f2fe',
                              border: '1px solid #bae6fd',
                              color: '#0369a1',
                              padding: '3px 8px',
                              borderRadius: 14,
                              fontSize: 12,
                              fontWeight: 600
                            }}
                          >
                            <span>{cat ? cat.name : `Kategoria ID ${cid}`}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveCategoryFromEditor(cid)}
                              style={{ background: 'transparent', border: 'none', color: '#0284c7', cursor: 'pointer', fontWeight: 700, fontSize: 12, padding: 0 }}
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => setEditingSticker(prev => ({ ...prev, categoryIds: [] }))}
                        style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '3px 6px' }}
                      >
                        Pastro të gjitha
                      </button>
                    </div>
                  )}

                  {/* Search categories */}
                  <input
                    type="text"
                    placeholder="🔍 Kërko kategori me emër ose ID..."
                    value={catSearch}
                    onChange={(e) => setCatSearch(e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '7px 10px',
                      fontSize: 12.5,
                      border: '1px solid #cbd5e1',
                      borderRadius: 6,
                      marginBottom: 8
                    }}
                  />

                  {/* Categories scrollable list */}
                  <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: 6, padding: 4 }}>
                    {filteredCategories.length === 0 ? (
                      <div style={{ fontSize: 12, color: '#94a3b8', padding: 8, textAlign: 'center' }}>
                        Nuk u gjet asnjë kategori me këtë kërkim.
                      </div>
                    ) : (
                      filteredCategories.map(cat => {
                        const isChecked = (editingSticker.categoryIds || []).map(String).includes(cat.id);
                        return (
                          <label
                            key={cat.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '5px 8px',
                              borderRadius: 4,
                              cursor: 'pointer',
                              background: isChecked ? '#f0f9ff' : 'transparent',
                              fontSize: 12.5,
                              paddingLeft: `${cat.depth * 14 + 8}px`
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggleCategory(cat.id)}
                              style={{ accentColor: '#007da5' }}
                            />
                            <span style={{ fontWeight: cat.depth === 0 ? 700 : 500, color: '#1e293b' }}>
                              {cat.depth > 0 && <span style={{ color: '#94a3b8' }}>↳ </span>}
                              {cat.name}
                            </span>
                            <span style={{ fontSize: 10, color: '#94a3b8' }}>(ID: {cat.id})</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* SECTION B: Product SKUs */}
                <div style={{ background: '#fff', padding: 14, borderRadius: 8, border: '1px solid #cbd5e1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                      🏷️ Sipas Produkteve / Kodeve SKU ({(editingSticker.skus || []).length} të caktuara)
                    </div>
                  </div>

                  {/* Bulk Input for SKUs */}
                  <form onSubmit={handleAddCandidateSkus} style={{ marginBottom: 10 }}>
                    <textarea
                      rows={2}
                      className="admin-input"
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        fontFamily: 'monospace',
                        fontSize: 12,
                        lineHeight: '1.4',
                        padding: '8px 10px',
                        borderRadius: 6,
                        border: '1px solid #cbd5e1',
                        resize: 'vertical'
                      }}
                      placeholder="Vendosni një ose më shumë kode SKU (të ndara me rresht, presje, ose hapësirë). Mund të ngjisni direkt nga Excel..."
                      value={bulkSkuInput}
                      onChange={(e) => setBulkSkuInput(e.target.value)}
                    />

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                      <div style={{ fontSize: 11, color: parsedCandidateSkus.length > 0 ? '#15803d' : '#64748b', fontWeight: parsedCandidateSkus.length > 0 ? 700 : 400 }}>
                        {parsedCandidateSkus.length > 0 ? (
                          <span>✨ {parsedCandidateSkus.length} kode SKU të identifikuara</span>
                        ) : (
                          <span>💡 P.sh. B2TVNL55..., B2WMSMS...</span>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={!parsedCandidateSkus.length}
                        style={{
                          background: parsedCandidateSkus.length ? '#007da5' : '#94a3b8',
                          color: '#fff',
                          border: 'none',
                          padding: '5px 14px',
                          borderRadius: 6,
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: parsedCandidateSkus.length ? 'pointer' : 'not-allowed'
                        }}
                      >
                        + Shto {parsedCandidateSkus.length > 1 ? `${parsedCandidateSkus.length} SKU` : 'SKU'}
                      </button>
                    </div>
                  </form>

                  {/* Active SKUs list & search */}
                  {(editingSticker.skus || []).length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <input
                          type="text"
                          placeholder="🔍 Kërko SKU në listë..."
                          value={skuSearch}
                          onChange={(e) => setSkuSearch(e.target.value)}
                          style={{ padding: '4px 8px', fontSize: 11.5, border: '1px solid #cbd5e1', borderRadius: 4, width: 160 }}
                        />
                        <button
                          type="button"
                          onClick={handleClearAllSkusFromEditor}
                          style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                        >
                          Pastro listën e SKU
                        </button>
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 130, overflowY: 'auto', padding: 6, background: '#f8fafc', borderRadius: 6, border: '1px solid #f1f5f9' }}>
                        {(editingSticker.skus || [])
                          .filter(s => !skuSearch.trim() || s.toLowerCase().includes(skuSearch.toLowerCase().trim()))
                          .map(sku => (
                            <div
                              key={sku}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                background: '#fff',
                                border: '1px solid #cbd5e1',
                                padding: '2px 8px',
                                borderRadius: 12,
                                fontSize: 11,
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                color: '#1e293b'
                              }}
                            >
                              <span>{sku}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveSkuFromEditor(sku)}
                                style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 11, padding: 0 }}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

              </div>

            </div>

            {/* RIGHT COLUMN: Live Interactive Mock Product Card Preview */}
            <div style={{ position: 'sticky', top: 20 }}>
              <div style={{
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: 14,
                padding: 20,
                boxShadow: '0 4px 16px rgba(0,0,0,0.04)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                    👁️ Shikimi Paraprak në Kohë Reale (Live Card Preview)
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: 10 }}>
                    Interaktiv
                  </span>
                </div>
                <p style={{ margin: '0 0 16px', fontSize: 12, color: '#64748b' }}>
                  Kjo është mënyra e saktë se si do të duket sticker-i në kartën e produktit në faqe.
                </p>

                {/* THE MOCK PRODUCT CARD CONTAINER */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div
                    style={{
                      width: 280,
                      background: '#fff',
                      borderRadius: 12,
                      border: '1.5px solid #e2e8f0',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                      padding: 16,
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                      userSelect: 'none'
                    }}
                  >
                    {/* THE STICKER ITSELF RENDERING LIVE */}
                    <ProductSticker sticker={editingSticker} isPreview={true} />

                    {/* Default Mock Discount Badge (to see how they coexist!) */}
                    <div style={{
                      position: 'absolute',
                      top: 12,
                      left: 12,
                      background: '#f48d21',
                      color: '#fff',
                      padding: '3px 8px',
                      borderRadius: 6,
                      fontSize: 10,
                      fontWeight: 800,
                      zIndex: 4,
                      opacity: editingSticker.position === 'top-left' && editingSticker.offsetY < 35 && editingSticker.offsetX < 60 ? 0.4 : 1
                    }}>
                      -20%
                    </div>

                    {/* Mock Card Header */}
                    <div style={{ marginTop: 24 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        GLOBE ELECTRONICS
                      </span>
                      <h4 style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
                        Smart TV 55" UHD 4K Crystal HDR
                      </h4>
                    </div>

                    {/* Mock Image Area */}
                    <div style={{
                      height: 160,
                      background: '#f8fafc',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px dashed #cbd5e1'
                    }}>
                      <img
                        src={sampleCardImg}
                        alt="Product Preview"
                        style={{ maxWidth: '80%', maxHeight: 130, objectFit: 'contain' }}
                      />
                    </div>

                    {/* Mock Price */}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontSize: 12, color: '#94a3b8', textDecoration: 'line-through' }}>599,00 €</span>
                      <span style={{ fontSize: 18, fontWeight: 900, color: '#007da5' }}>479,00 €</span>
                    </div>

                    {/* Mock Button */}
                    <button
                      type="button"
                      disabled
                      style={{
                        background: '#007da5',
                        color: '#fff',
                        border: 'none',
                        padding: '8px 0',
                        borderRadius: 8,
                        fontWeight: 700,
                        fontSize: 12,
                        textAlign: 'center',
                        cursor: 'default'
                      }}
                    >
                      BLI TANI
                    </button>
                  </div>
                </div>

                {/* Coordinate indicator */}
                <div style={{ marginTop: 16, padding: 10, background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 11.5, color: '#475569' }}>
                  <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>📍 Parametrat Aktivë:</div>
                  <div>Pozicioni: <strong>{editingSticker.position || 'top-left'}</strong></div>
                  <div>Zhvendosja Y: <strong>{editingSticker.offsetY || 0}px</strong> | Zhvendosja X: <strong>{editingSticker.offsetX || 0}px</strong></div>
                  <div>Rrotullimi: <strong>{editingSticker.rotation || 0}°</strong> | Madhësia: <strong>{editingSticker.type === 'image' ? `${editingSticker.imageWidth}px` : `${editingSticker.fontSize}px font`}</strong></div>
                </div>

                {/* Save button shortcut */}
                <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={handleSaveSticker}
                    style={{
                      width: '100%',
                      background: '#007da5',
                      color: '#fff',
                      border: 'none',
                      padding: '10px 0',
                      borderRadius: 8,
                      fontWeight: 800,
                      fontSize: 13,
                      cursor: saving ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {saving ? 'Duke ruajtur...' : '💾 Ruaj Sticker-in'}
                  </button>
                </div>

              </div>
            </div>

          </div>

        </div>
      )}

      {/* STICKERS LIST TABLE / CARDS (WHEN NOT IN EDITOR OR UNDERNEATH) */}
      {!editingSticker && (
        <div className="admin-card" style={{ padding: 24, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
              📋 Lista e Stickers të Konfiguruar ({stickers.length})
            </h3>
            <span style={{ fontSize: 12, color: '#64748b' }}>
              Mund të aktivizoni/çaktivizoni, ndryshoni ose duplikoni çdo sticker.
            </span>
          </div>

          {stickers.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: '#f8fafc',
              borderRadius: 12,
              border: '2px dashed #cbd5e1'
            }}>
              <div style={{ fontSize: 42, marginBottom: 12 }}>🏷️</div>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                Nuk keni krijuar asnjë sticker ende
              </h4>
              <p style={{ margin: '8px 0 20px', fontSize: 13, color: '#64748b', maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
                Shtoni etiketa me zbritje, njoftime fushatash, ose logo speciale që shfaqen me saktësi pikselash mbi kartat e produkteve.
              </p>
              <button
                type="button"
                onClick={handleAddNew}
                style={{
                  background: '#007da5',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                + Krijo Sticker-in e Parë
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 18 }}>
              {stickers.map((stk) => {
                const categoryNames = (stk.categoryIds || []).map(cid => categoryMap.get(String(cid))?.name || `ID ${cid}`);
                const isImg = stk.type === 'image';

                return (
                  <div
                    key={stk.id}
                    style={{
                      background: '#fff',
                      border: stk.enabled ? '1.5px solid #cbd5e1' : '1px dashed #cbd5e1',
                      borderRadius: 12,
                      padding: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: 14,
                      opacity: stk.enabled ? 1 : 0.65,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div>
                      {/* Top Header of Card */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>
                            {stk.name}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            {isImg ? '🖼️ Imazh grafik' : '🏷️ Tekst badge'} • Pozicioni: <strong>{stk.position || 'top-left'}</strong> ({stk.offsetY || 0}px Y, {stk.offsetX || 0}px X)
                          </div>
                        </div>

                        {/* Status pill toggle */}
                        <button
                          type="button"
                          onClick={() => handleToggleEnable(stk.id)}
                          style={{
                            background: stk.enabled ? '#dcfce7' : '#f1f5f9',
                            color: stk.enabled ? '#15803d' : '#64748b',
                            border: `1px solid ${stk.enabled ? '#86efac' : '#cbd5e1'}`,
                            padding: '3px 9px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          {stk.enabled ? '● AKTIV' : '○ JO-AKTIV'}
                        </button>
                      </div>

                      {/* Visual Preview Box */}
                      <div style={{
                        height: 70,
                        background: '#f8fafc',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 12
                      }}>
                        <ProductSticker sticker={stk} isPreview={true} containerStyle={{ position: 'absolute' }} />
                        <span style={{ fontSize: 10, color: '#cbd5e1', fontWeight: 600 }}>Zona e Kartës</span>
                      </div>

                      {/* Targeting Info */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#475569' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 600, color: '#334155' }}>Kategoritë:</span>
                          {(stk.categoryIds || []).length === 0 ? (
                            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Asnjë kategori e përzgjedhur</span>
                          ) : (
                            <span style={{ fontWeight: 700, color: '#0369a1' }}>
                              {(stk.categoryIds || []).length} kategori {stk.includeSubcategories !== false && '(me nënkategoritë)'}
                              {categoryNames.length > 0 && (
                                <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 4 }}>
                                  ({categoryNames.slice(0, 2).join(', ')}{categoryNames.length > 2 ? '...' : ''})
                                </span>
                              )}
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 600, color: '#334155' }}>Kodet SKU:</span>
                          {(stk.skus || []).length === 0 ? (
                            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Asnjë SKU specifik</span>
                          ) : (
                            <span style={{ fontWeight: 700, color: '#15803d' }}>
                              {(stk.skus || []).length} produkte SKU
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions Row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => handleEdit(stk)}
                          style={{
                            background: '#007da5',
                            color: '#fff',
                            border: 'none',
                            padding: '6px 14px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          <span>✏️ Ndrysho</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDuplicate(stk)}
                          title="Dupliko këtë sticker"
                          style={{
                            background: '#f8fafc',
                            color: '#334155',
                            border: '1px solid #cbd5e1',
                            padding: '6px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          📋 Dupliko
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDelete(stk.id, stk.name)}
                        title="Fshij sticker-in"
                        style={{
                          background: '#fff',
                          color: '#dc2626',
                          border: '1px solid #fecaca',
                          padding: '6px 10px',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        🗑️ Fshij
                      </button>
                    </div>

                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
